/**
 * Service pour gérer l'accès au Dashboard Express de Stripe
 * Génère des login links pour permettre aux organisations d'accéder à leur dashboard
 */

import { FUNCTIONS_BASE_URL } from '@/shared/config/environment';
import { toast } from 'react-hot-toast';

interface ExpressLoginLinkResponse {
  success: boolean;
  url: string;
  created: number;
}

interface ExpressLoginLinkError {
  error: string;
  details?: string;
}

export class StripeExpressDashboardService {
  /**
   * Génère un login link pour accéder au dashboard Express d'un compte Connect
   * @param stripeAccountId - ID du compte Stripe Connect
   * @returns L'URL du login link ou null en cas d'erreur
   */
  static async createExpressLoginLink(stripeAccountId: string): Promise<string | null> {
    try {
      console.log(`🔐 Génération du login link pour le compte: ${stripeAccountId}`);
      
      const response = await fetch(
        `${FUNCTIONS_BASE_URL}/createStripeExpressLoginLink?accountId=${encodeURIComponent(stripeAccountId)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const errorData: ExpressLoginLinkError = await response.json();
        
        if (response.status === 404) {
          toast.error('Compte Stripe non trouvé ou accès non autorisé');
          console.error('❌ Compte Stripe non trouvé:', errorData);
        } else {
          toast.error('Erreur lors de la génération du lien de connexion');
          console.error('❌ Erreur génération login link:', errorData);
        }
        
        return null;
      }

      const data: ExpressLoginLinkResponse = await response.json();
      
      if (data.success && data.url) {
        console.log('✅ Login link généré avec succès');
        return data.url;
      }

      toast.error('Réponse invalide du serveur');
      console.error('❌ Réponse invalide:', data);
      return null;

    } catch (error) {
      console.error('❌ Erreur réseau lors de la génération du login link:', error);
      toast.error('Erreur de connexion au serveur');
      return null;
    }
  }

  /**
   * Ouvre le dashboard Express dans une nouvelle fenêtre
   * @param stripeAccountId - ID du compte Stripe Connect
   * @returns true si l'ouverture a réussi, false sinon
   */
  static async openExpressDashboard(stripeAccountId: string): Promise<boolean> {
    try {
      toast.loading('Génération du lien de connexion...', { id: 'express-login' });
      
      const loginUrl = await this.createExpressLoginLink(stripeAccountId);
      
      if (!loginUrl) {
        toast.dismiss('express-login');
        return false;
      }

      // Ouvrir le dashboard dans une nouvelle fenêtre
      const newWindow = window.open(loginUrl, '_blank', 'noopener,noreferrer');
      
      if (!newWindow || newWindow.closed) {
        toast.error('Impossible d\'ouvrir le dashboard. Veuillez autoriser les pop-ups.', { 
          id: 'express-login' 
        });
        return false;
      }

      toast.success('Dashboard Express ouvert dans une nouvelle fenêtre', { 
        id: 'express-login' 
      });
      
      return true;

    } catch (error) {
      console.error('❌ Erreur lors de l\'ouverture du dashboard:', error);
      toast.error('Erreur lors de l\'ouverture du dashboard', { id: 'express-login' });
      return false;
    }
  }
}


